/** Visualizador do estádio (item 3.1): o cenário completo com 14 atletas parados em formação, para escala. */
import * as THREE from "three";
import { loadModels, type AthleteSpec } from "@besenliga/athlete-three";
import { randomAppearance } from "@besenliga/content";
import { BENCH_SECONDS, gpuName, summarizeFrames, type BenchResult } from "./bench";
import { BlobShadows, animateRider, createRider, updateRiderLod, type Rider as MountedRider } from "./riders";
import {
  SUN_DIRECTION,
  addStadiumLights,
  buildStadium,
  followSun,
  Random,
  type Stadium,
} from "@besenliga/stadium-three";
import {
  EXAMPLE_KITS,
  type GraphicsSettings,
  PITCH,
  seedRng,
  type Build,
  type Height,
  type Kit,
  type Position,
} from "@besenliga/shared";

export type CameraMode = "aerea" | "tatica" | "cinematica";

/** Câmeras da cena de referência: distância, ângulo a partir da vertical e direção inicial. */
export const CAMERAS: Record<
  CameraMode,
  { dist: number; phi: number; theta: number; follow: boolean; orbit: boolean }
> = {
  aerea: { dist: 42, phi: 0.72, theta: -0.5, follow: true, orbit: false },
  tatica: { dist: 128, phi: 0.18, theta: -Math.PI / 2, follow: false, orbit: false },
  cinematica: { dist: 92, phi: 1.05, theta: -0.5, follow: false, orbit: true },
};

/** Formação de exibição: posição [x, altura, z] de cada atleta do time da casa, no lado x < 0 (o visitante é espelhado). */
const FORMATION: { pos: Position; at: [number, number, number] }[] = [
  { pos: "goleiro", at: [-50, 15, 0] },
  { pos: "artilheiro", at: [-12, 9, -10] },
  { pos: "artilheiro", at: [-9, 10, 0] },
  { pos: "artilheiro", at: [-12, 9, 10] },
  { pos: "batedor", at: [-24, 12, -16] },
  { pos: "batedor", at: [-24, 12, 16] },
  { pos: "cacador", at: [-34, 22, 4] },
];
const HEIGHTS: Height[] = ["baixo", "medio", "alto"];
const BUILDS: Build[] = ["magro", "medio", "forte", "gordo"];

interface Rider extends MountedRider {
  base: THREE.Vector3;
  yaw: number;
  phase: number;
}

export class StadiumView {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(42, 1, 0.5, 2000);
  private readonly stadium: Stadium;
  private readonly sun: THREE.DirectionalLight;
  private readonly riders: Rider[] = [];
  private readonly target = new THREE.Vector3(0, 4, 0);
  private mode: CameraMode = "aerea";
  private dist = CAMERAS.aerea.dist;
  private phi = CAMERAS.aerea.phi;
  private theta = CAMERAS.aerea.theta;
  private drag: { x: number; y: number } | null = null;
  private frame = 0;
  private time = 0;
  private last = 0;
  private paused = false;
  /** Rodada de medição em andamento: tempos de quadro e quando termina. */
  private bench: { frames: number[]; until: number; done: (r: BenchResult) => void } | null = null;
  /** Na medição completa a câmera gira em volta da jogada; na medição automática, nada muda na tela. */
  private benchTour = true;
  private readonly resizeObserver: ResizeObserver;
  /** Medidas do último quadro, para o painel de orçamento. */
  readonly info = { calls: 0, triangles: 0, fps: 0 };
  private fpsFrames = 0;
  private fpsSince = 0;
  private blobs: BlobShadows | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly settings: GraphicsSettings,
  ) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: settings.antialias,
      powerPreference: "high-performance",
    });
    this.renderer.setPixelRatio(
      Math.min(window.devicePixelRatio, settings.maxPixelRatio) * settings.resolutionScale,
    );
    this.renderer.shadowMap.enabled = settings.shadowMapSize > 0;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.info.autoReset = false;
    this.sun = addStadiumLights(this.scene, { shadowSize: settings.shadowMapSize });
    this.stadium = buildStadium({
      home: EXAMPLE_KITS["corujas-campinas"],
      away: EXAMPLE_KITS["lobos-guaiba"],
      anisotropy: this.renderer.capabilities.getMaxAnisotropy(),
      animatedCrowd: settings.animatedCrowd,
      occupancy: settings.crowdOccupancy,
      trees: settings.trees,
    });
    this.scene.add(this.stadium.root);
    this.stadium.bakeShadows(this.renderer, SUN_DIRECTION, settings.bakeSize);
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.resize();
    canvas.addEventListener("pointerdown", this.onDown);
    canvas.addEventListener("pointermove", this.onMove);
    canvas.addEventListener("pointerup", this.onUp);
    canvas.addEventListener("pointercancel", this.onUp);
    canvas.addEventListener("wheel", this.onWheel, { passive: false });
    this.frame = requestAnimationFrame(this.tick);
  }

  async loadAthletes(modelsUrl: string): Promise<void> {
    const models = await loadModels(modelsUrl);
    const rnd = new Random(14);
    for (const [side, kit] of [
      [-1, EXAMPLE_KITS["corujas-campinas"]],
      [1, EXAMPLE_KITS["lobos-guaiba"]],
    ] as [number, Kit][]) {
      FORMATION.forEach((f, i) => {
        const sexo = (i + (side > 0 ? 1 : 0)) % 2 === 0 ? "homem" : "mulher";
        const [, look] = randomAppearance(seedRng(1000 + i * 7 + (side > 0 ? 100 : 0)), sexo);
        const spec: AthleteSpec = {
          sexo,
          altura: rnd.pick(HEIGHTS),
          biotipo: rnd.pick(BUILDS),
          posicao: f.pos,
          ...look,
        };
        const rider = createRider(models, spec, kit, this.settings);
        const base = new THREE.Vector3(f.at[0] * -side, f.at[1], f.at[2] * -side);
        rider.holder.position.copy(base);
        this.scene.add(rider.holder);
        // Todos olham para o meio do campo.
        this.riders.push({
          ...rider,
          base,
          yaw: side < 0 ? Math.PI / 2 : -Math.PI / 2,
          phase: rnd.range(0, 6),
        });
      });
    }
    if (this.settings.shadowMapSize === 0) {
      this.blobs = new BlobShadows(this.riders.length);
      this.scene.add(this.blobs.mesh);
    }
  }

  setMode(mode: CameraMode): void {
    this.mode = mode;
    const c = CAMERAS[mode];
    this.dist = c.dist;
    this.phi = c.phi;
    this.theta = c.theta;
  }

  setPaused(paused: boolean): void {
    this.paused = paused;
  }

  /** Coloca a cena num instante fixo (para capturas reproduzíveis). */
  freezeAt(time: number): void {
    this.paused = true;
    this.time = time;
  }

  stats() {
    return this.stadium.stats();
  }

  /**
   * Mede o desempenho por BENCH_SECONDS na câmera de jogo, girando em volta da jogada, com a cena em movimento.
   * Serve para o aceite do item 3.2 no notebook de referência.
   */
  measure({ tour = true } = {}): Promise<BenchResult & { gpu: string; width: number; height: number }> {
    if (tour) this.setMode("aerea");
    this.benchTour = tour;
    this.paused = false;
    return new Promise((resolve) => {
      this.bench = {
        frames: [],
        until: this.last + BENCH_SECONDS + 1,
        done: (r) =>
          resolve({
            ...r,
            gpu: gpuName(this.renderer.getContext()),
            width: this.renderer.domElement.width,
            height: this.renderer.domElement.height,
          }),
      };
    });
  }

  dispose(): void {
    cancelAnimationFrame(this.frame);
    this.resizeObserver.disconnect();
    this.canvas.removeEventListener("pointerdown", this.onDown);
    this.canvas.removeEventListener("pointermove", this.onMove);
    this.canvas.removeEventListener("pointerup", this.onUp);
    this.canvas.removeEventListener("pointercancel", this.onUp);
    this.canvas.removeEventListener("wheel", this.onWheel);
    this.stadium.dispose();
    this.blobs?.dispose();
    this.renderer.dispose();
    this.renderer.forceContextLoss();
  }

  private resize(): void {
    const { clientWidth: w, clientHeight: h } = this.canvas;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  private readonly tick = (nowMs: number): void => {
    this.frame = requestAnimationFrame(this.tick);
    const now = nowMs / 1000;
    if (this.bench) {
      if (this.last) this.bench.frames.push(nowMs - this.last * 1000);
      if (now >= this.bench.until) {
        const { frames, done } = this.bench;
        this.bench = null;
        done(summarizeFrames(frames));
      }
    }
    const dt = Math.min(0.05, this.last ? now - this.last : 0);
    this.last = now;
    if (!this.paused) this.time += dt;
    const t = this.time;
    this.stadium.update(t);

    for (const r of this.riders) {
      r.holder.position.set(r.base.x, r.base.y + Math.sin(t * 1.4 + r.phase) * 0.35, r.base.z);
      animateRider(r, t + r.phase, r.yaw, { turnRate: 0, climb: 0, speed: 0, turbo: false });
    }
    this.blobs?.update(this.riders);

    const cam = CAMERAS[this.mode];
    const lead = this.riders[2];
    const focus =
      cam.follow && lead
        ? lead.holder.position.clone().setY(lead.holder.position.y * 0.4)
        : new THREE.Vector3(0, 4, 0);
    this.target.lerp(focus, this.paused ? 1 : 0.08);
    const touring = this.bench !== null && this.benchTour;
    if ((cam.orbit || touring) && !this.paused && !this.drag) this.theta += dt * (touring ? 0.25 : 0.06);
    const d = this.dist * (this.camera.aspect < 1 ? Math.min(2.2, 0.85 / this.camera.aspect) : 1);
    this.camera.position.set(
      this.target.x + d * Math.sin(this.phi) * Math.sin(this.theta),
      this.target.y + d * Math.cos(this.phi),
      this.target.z + d * Math.sin(this.phi) * Math.cos(this.theta),
    );
    this.camera.lookAt(this.target);
    followSun(this.sun, this.target, cam.follow ? 45 : 100);

    for (const r of this.riders) updateRiderLod(r, this.camera, this.settings);

    this.renderer.info.reset();
    this.renderer.render(this.scene, this.camera);
    this.info.calls = this.renderer.info.render.calls;
    this.info.triangles = this.renderer.info.render.triangles;
    this.fpsFrames++;
    if (now - this.fpsSince >= 1) {
      this.info.fps = Math.round(this.fpsFrames / (now - this.fpsSince));
      this.fpsFrames = 0;
      this.fpsSince = now;
    }
  };

  private readonly onDown = (e: PointerEvent) => {
    this.drag = { x: e.clientX, y: e.clientY };
    this.canvas.setPointerCapture(e.pointerId);
  };

  private readonly onMove = (e: PointerEvent) => {
    if (!this.drag) return;
    this.theta -= (e.clientX - this.drag.x) * 0.006;
    this.phi = THREE.MathUtils.clamp(this.phi - (e.clientY - this.drag.y) * 0.004, 0.08, 1.35);
    this.drag = { x: e.clientX, y: e.clientY };
  };

  private readonly onUp = (e: PointerEvent) => {
    this.drag = null;
    if (this.canvas.hasPointerCapture(e.pointerId)) this.canvas.releasePointerCapture(e.pointerId);
  };

  private readonly onWheel = (e: WheelEvent) => {
    e.preventDefault();
    this.dist = THREE.MathUtils.clamp(this.dist * (1 + Math.sign(e.deltaY) * 0.08), 20, PITCH.radiusX * 3.4);
  };
}
